import { Table, Row, Modal, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import classNames from 'classnames';
import React from 'react';
import Highlighter from 'react-highlight-words';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useSortData } from '.';
import { EInvoiceButton, IconButton } from '../../components/button';
import { ICSort, ICAdd, ICNoneData } from '../../icons';
import { ICMaterialSymbol } from '../../icons/material-symbol';
import { UserForm } from './create';
import { useNotification } from '../../hooks/useNotification';
import { setLoading } from '../../redux/slices/appInfo';
import {
    Direction,
    IPagination,
    SearchQueryParams,
} from '../../interface/paging';
import { removeUndefinedAttribute } from '../../utils';
import debouce from 'lodash/debounce';
import { useScreenSize } from '../../hooks/useScreenSize';
import { TUsers } from './type';
import { EInvoicePagination } from '../../components/pagination';
import { createUser, deleteUser, updateUser } from '../../api/setting/users';

const ActionMenu = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    cursor: pointer;
    padding-right: 16px;
    &:hover {
        background-color: #f7f8fa;
    }
    button {
        padding: 0 !important;
    }
`;

const WrapperTable = styled.div`
    .ant-table-thead > tr > th {
        background-color: white;
    }
    .data-row:nth-child(odd) {
        background-color: #f7f8fa;
    }
    .ant-table-column-sorters .ant-table-column-sorter-inner {
        .anticon {
            display: none;
        }
    }
`;

const WrapperInput = styled.div`
    @media (max-width: 768px) {
    .ant-input-group-wrapper {
        width: 100% !important;
    }
`;

const WrapperButton = styled.div`
    @media (max-width: 768px) {
        width: 100%;
        .ant-btn {
            width: 100% !important;
        }
    }
`;

const TitleRenderer: React.FC<{ title: string }> = ({ title }) => {
    const keyword = React.useContext(KeywordContext);

    return (
        <Highlighter
            highlightClassName="text-highlight"
            searchWords={[`${keyword || ''}`]}
            autoEscape={true}
            className="text-body text-high-em"
            textToHighlight={title || ''}
        />
    );
};
type SortableColumnProps = {
    title: string;
    order?: SortOrder;
    name: string;
};
const SortableColumn: React.FC<SortableColumnProps> = ({
    title,
    order,
    name,
}) => {
    const { setSortData } = useSortData();
    return (
        <div
            className="flex space-x-3 items-center justify-between select-none group"
            onClick={() => {
                setSortData({
                    field: order === 'descend' ? 'createdDate' : name,
                    order:
                        order === 'descend' ? 'desc' : !order ? 'asc' : 'desc',
                });
            }}
        >
            <span className="text-body-bold text-high-em">
                {title as string}
            </span>
            <ICSort order={order} />
        </div>
    );
};

type UsersPageListProps = {
    userList: TUsers[];
    setUserList: (userServiceList: TUsers[]) => void;
    keyword?: string;
    pagination?: IPagination;
    onChangePaging: ({ page }: { page: number }) => void;
    onReload: () => void;
    filterParams: SearchQueryParams;
    onFilterData: (payload: SearchQueryParams) => void;
};
const KeywordContext = React.createContext<string>('');

export const UsersPageList: React.FC<UsersPageListProps> = ({
    userList,
    setUserList,
    keyword,
    pagination,
    onChangePaging,
    onReload,
    filterParams,
    onFilterData,
}) => {
    const size = useScreenSize();
    const mobileMode = size === 'xs' || size === 'sm';
    const dispatch = useDispatch();
    const { showSuccess, showError } = useNotification();
    const onChangePage = (page: number) => {
        onChangePaging({ page });
    };
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [userId, setUserId] = React.useState<string | undefined>('');
    const [showUserServiceDialog, setShowUserServiceDialog] =
        React.useState(false);
    const [userSelected, setUserSelected] = React.useState<TUsers | undefined>(
        undefined
    );
    const [form] = useForm();

    // handle search keyword
    const [searchWord, setSearchWord] = React.useState<string | undefined>('');
    const onSearchData = React.useCallback(
        async (searchWord: string | undefined, searchColumnTemp?: string) => {
            try {
                const queryParams: SearchQueryParams = {
                    page: 1,
                    pageSize: filterParams?.pageSize || 10,
                    // searchWord: searchWord,
                    sortBy: Direction.ASC,
                    // searchColum: searchColumnTemp || searchColumn,
                };
                const newPayload = removeUndefinedAttribute(queryParams);
                onFilterData(newPayload);
            } catch (error) {
                // Handle error
            }
        },
        [filterParams]
    );

    const handleSearchData = (value: string | undefined) => {
        onSearchKeyword(value);
        setSearchWord(value);
    };

    const onSearchKeyword = React.useMemo(() => {
        return debouce(onSearchData, 600);
    }, [onSearchData]);

    React.useEffect(() => {
        return () => {
            onSearchKeyword.cancel();
        };
    }, [onSearchKeyword]);
    // end handle search keyword

    const onShowDeleteModal = React.useCallback(
        (id: string) => {
            setUserId(id);
            setShowDeleteModal(true);
        },
        [setShowDeleteModal]
    );

    const columns: ColumnsType<TUsers> = [
        {
            title: 'Action',
            dataIndex: 'id',
            key: 'id',
            width: '40px',
            render: (userId: string, data: TUsers) => {
                return (
                    <ActionMenu>
                        <IconButton
                            icon={<ICMaterialSymbol icon="edit_square" />}
                            btnType="tertiary"
                            onClick={() => {
                                setUserSelected(data);
                                setShowUserServiceDialog(true);
                            }}
                        />
                        <IconButton
                            icon={<ICMaterialSymbol icon="delete" />}
                            onClick={() => onShowDeleteModal(userId)}
                            btnType="tertiary"
                        />
                    </ActionMenu>
                );
            },
        },
        {
            title: 'Name',
            dataIndex: 'username',
            field: 'username',
            key: 'username',
            sorter: true,
            render: (title: string) => {
                return <TitleRenderer title={title} />;
            },
        },
        {
            title: 'Type',
            dataIndex: 'type',
            field: 'type',
            key: 'type',
            sorter: true,
            render: (title: string) => {
                return <TitleRenderer title={title} />;
            },
        },
    ].map((col: ColumnType<TUsers>) => {
        if (col.sorter) {
            const oldTitle = col.title;
            col.title = ({ sortColumns }) => {
                const sortedColumn = sortColumns?.find(
                    ({ column }) => column.key === col.key
                );
                return (
                    <SortableColumn
                        title={oldTitle as string}
                        order={sortedColumn?.order}
                        name={col.dataIndex as string}
                    />
                );
            };
        } else {
            col.title = (
                <div className="text-body-bold text-high-em">
                    {col.title as string}
                </div>
            );
        }
        col.showSorterTooltip = false;
        return col;
    });

    const doSaveUser = React.useCallback(
        async (userId: string | undefined, payload: TUsers) => {
            try {
                dispatch(setLoading(true));
                if (userId) {
                    await updateUser(userId, payload);
                    showSuccess('Update User success', 'User has been updated');
                } else {
                    await createUser(payload);
                    showSuccess('Save User success', 'User has been created');
                }
                onReload();
                setShowUserServiceDialog(false);
            } catch (error: any) {
                const errorMessage =
                    error?.response?.data?.detail ||
                    'Please check your input data';
                showError('Save User failed', errorMessage);
            } finally {
                dispatch(setLoading(false));
            }
        },
        [userList, dispatch, showSuccess, showError]
    );

    const onSaveUsers = React.useCallback(() => {
        form.validateFields()
            .then(values => {
                const userData = {
                    ...userSelected,
                    ...values,
                };
                if (userSelected) {
                    doSaveUser(userData.id, userData);
                } else {
                    doSaveUser(undefined, userData);
                }
            })
            .catch(() => {
                showError('Save User failed', 'Please check your input data');
            });
    }, [form, userList, userSelected, showError]);

    const onDeleteUser = React.useCallback(async () => {
        if (!userId) {
            return;
        }
        try {
            dispatch(setLoading(true));
            await deleteUser(userId);
            setShowDeleteModal(false);
            showSuccess('Delete User success', 'User has been deleted');
            onReload();
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.detail ||
                'Error occurred while deleting UserService';
            showError('Delete User Service failed', errorMessage);
        } finally {
            dispatch(setLoading(false));
        }
    }, [dispatch, showSuccess, userId, showError]);

    return (
        <WrapperTable className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
            <div className="flex w-full px-[12px] py-[20px] shadow-e-03 max-md:justify-center items-center max-md:flex-wrap gap-3 justify-between bg-white rounded-[12px]">
                <div className="flex gap-3 items-center justify-center w-full flex-wrap">
                    <WrapperInput className="flex items-center w-full gap-2">
                        <Input
                            placeholder="Search by ..."
                            value={searchWord}
                            onChange={e => handleSearchData(e.target.value)}
                            style={{
                                width: '100%',
                            }}
                            size="large"
                        />
                    </WrapperInput>
                </div>
                <WrapperButton className="flex gap-[10px]">
                    <EInvoiceButton
                        btnType="primary"
                        onClick={() => {
                            setUserSelected(undefined);
                            setShowUserServiceDialog(true);
                        }}
                        style={{
                            width: '220px',
                        }}
                        btnSize="md"
                    >
                        <div className="flex space-x-2 items-center">
                            <ICAdd stroke="#fff" width={18} height={18} />
                            <span>Create new User</span>
                        </div>
                    </EInvoiceButton>
                </WrapperButton>
            </div>
            <div
                className={classNames([
                    'flex flex-col w-full h-full rounded-[12px] overflow-hidden shadow-e-03',
                    !mobileMode && 'bg-white',
                ])}
            >
                <KeywordContext.Provider value={keyword ?? ''}>
                    <Table
                        key="user"
                        className={classNames('white-header w-full')}
                        columns={columns}
                        dataSource={userList}
                        rowClassName="cursor-pointer data-row"
                        rowKey="id"
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: (
                                <EmptyTable
                                    keyword={keyword}
                                    onCreateNew={() => {
                                        setUserSelected(undefined);
                                        setShowUserServiceDialog(true);
                                    }}
                                />
                            ),
                        }}
                        pagination={false}
                    />
                </KeywordContext.Provider>
                {pagination?.totalPage && pagination.totalPage > 1 ? (
                    <Row
                        gutter={16}
                        style={{
                            margin: 0,
                        }}
                        className={classNames([
                            'grid items-center max-md:justify-center justify-end w-full bg-white p-[16px] border-t border-solid border-outline-med rounded-b-[12px]',
                            'max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-10 max-md:rounded-none',
                        ])}
                    >
                        <EInvoicePagination
                            onChange={onChangePage}
                            defaultCurrent={pagination?.page}
                            defaultPageSize={pagination?.pageSize}
                            total={pagination?.totalCount}
                            currentPage={pagination?.page}
                            showSizeChanger={false}
                        />
                    </Row>
                ) : null}
            </div>

            <Modal
                centered
                onCancel={() => setShowDeleteModal(false)}
                open={showDeleteModal}
                title={<div className="font-bold text-[18px]">Confirm</div>}
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <EInvoiceButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => setShowDeleteModal(false)}
                        >
                            Cancel
                        </EInvoiceButton>
                        <EInvoiceButton
                            btnType="danger"
                            btnSize="sm"
                            onClick={onDeleteUser}
                        >
                            Delete
                        </EInvoiceButton>
                    </div>
                }
            >
                <p>Are you sure you want to delete this user?</p>
            </Modal>

            <Modal
                centered
                onCancel={() => setShowUserServiceDialog(false)}
                open={showUserServiceDialog}
                maskClosable={false}
                destroyOnClose
                afterClose={() => form.resetFields()}
                title={
                    <div className="font-bold text-[18px]">
                        {userSelected ? 'Edit User' : 'New User Register'}
                    </div>
                }
                footer={
                    <div className="grid grid-cols-[auto_auto] justify-end gap-[16px]">
                        <EInvoiceButton
                            btnType="sub"
                            btnSize="sm"
                            onClick={() => setShowUserServiceDialog(false)}
                        >
                            Cancel
                        </EInvoiceButton>
                        <EInvoiceButton
                            btnType="primary"
                            btnSize="sm"
                            onClick={onSaveUsers}
                        >
                            Save
                        </EInvoiceButton>
                    </div>
                }
            >
                <div className="flex flex-col gap-[20px] w-full overflow-hidden h-full items-start box-border">
                    <UserForm userData={userSelected} form={form} />
                </div>
            </Modal>
        </WrapperTable>
    );
};

type IEmptyTableProps = {
    keyword: string | undefined;
    onCreateNew: () => void;
};

const EmptyTable: React.FC<IEmptyTableProps> = ({ keyword, onCreateNew }) => {
    return (
        <div className="my-6 flex flex-col gap-[5px]">
            <div className="flex justify-center">
                <ICNoneData />
            </div>
            <div className="text-standard-bold text-high-em">
                {keyword
                    ? `No data found with keyword “${keyword}”`
                    : 'No data found'}
            </div>
            <div className="flex justify-center">
                <span>
                    <EInvoiceButton
                        btnType="outline"
                        btnSize="sm"
                        style={{
                            borderRadius: '144px',
                        }}
                        onClick={() => onCreateNew()}
                    >
                        <div className="flex space-x-2 items-center">
                            <ICAdd stroke="#7357FF" width={18} height={18} />
                            <span>Create new User</span>
                        </div>
                    </EInvoiceButton>
                </span>
            </div>
        </div>
    );
};
